import { Link } from 'react-router-dom';
import { TREATMENTS, CLINIC_INFO } from '../data';
import { ChevronRight, Clock, Tag, Sparkles, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import { useEffect } from 'react';

export default function Treatments() {
  useEffect(() => {
    document.title = "CDH Sports- og Wellness Massage - Behandlinger";
  }, []);

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative h-[70vh] md:h-[75vh] lg:h-[80vh] flex items-center overflow-hidden pt-32 md:pt-40 lg:pt-48">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/767bb60f-8ec8-4a4c-940e-ed49b89257a3/f3a66a30-9f56-48f2-9421-a97b8c94d32c.jpg"
            alt="Behandlinger"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-blue-900/60 z-10" />
        </div>

        <div className="container mx-auto px-4 relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-[1.1]">
              Mine Behandlinger
            </h1>
            <p className="text-xs sm:text-sm md:text-base text-white/90 mb-10 leading-relaxed font-light">
              Jeg tilbyder et bredt udvalg af massageformer, der kan afhjælpe alt fra sportsskader til hverdagsstress. Find den behandling der passer til dig.
            </p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <a 
                href="#oversigt" 
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3.5 lg:px-8 lg:py-4 rounded-full text-base lg:text-lg font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Se alle behandlinger
              </a>
              <Link 
                to="/kontakt" 
                className="bg-white/10 hover:bg-white/20 backdrop-blur-md text-white border border-white/30 px-6 py-3.5 lg:px-8 lg:py-4 rounded-full text-base lg:text-lg font-bold transition-all flex items-center justify-center"
              >
                Book tid nu
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="oversigt" className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {[
              { id: "fysiurgisk-massage", category: "Dybdegående" },
              { id: "wellness-massage", category: "Velvære" },
              { id: "ir-sauna", category: "Velvære" },
              { id: "gravid-massage", category: "Andet" }
            ].map((item, idx) => {
              const treatment = TREATMENTS.find(t => t.id === item.id);
              if (!treatment) return null;

              return (
                <motion.div
                  key={treatment.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="group flex flex-col bg-white rounded-[2rem] overflow-hidden border border-blue-100 hover:border-emerald-200 hover:shadow-2xl transition-all duration-500"
                >
                  {/* Category Label */}
                  <div className="absolute top-6 left-6 z-20">
                    <span className="bg-white/90 backdrop-blur-sm text-blue-900 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-sm">
                      {item.category}
                    </span>
                  </div>

                  {/* Image Container */}
                  <div className="relative h-72 overflow-hidden">
                    <img
                      src={treatment.cardImage || treatment.image}
                      alt={treatment.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>

                  {/* Content */}
                  <div className="p-6 md:p-10 flex-grow flex flex-col">
                    <h3 className="text-2xl font-bold text-blue-900 mb-4 group-hover:text-blue-700 transition-colors">
                      {treatment.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-6 line-clamp-3">
                      {treatment.description}
                    </p>
                    
                    <div className="mt-auto space-y-3">
                      <Link
                        to={`/behandlinger/${treatment.id}`}
                        className="w-full inline-flex items-center justify-center bg-blue-50 text-[#3a38a4] hover:bg-blue-100 px-6 py-3.5 rounded-xl font-bold transition-all"
                      >
                        Læs mere om behandlingen
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Info/Guide Section */}
      <section className="py-24 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-[3rem] p-12 md:p-20 shadow-xl flex flex-col lg:flex-row items-center gap-16 border border-blue-100">
            <div className="lg:w-1/2">
              <h2 className="text-3xl font-bold text-blue-900 mb-6">Er du i tvivl om valget af behandling?</h2>
              <p className="text-gray-600 mb-8 text-base md:text-lg">
                Jeg ved, at det kan være svært at vælge den helt rigtige massageform, hvis man aldrig har prøvet det før eller har specifikke smerter. 
              </p>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-blue-100 p-2 rounded-lg mr-4 mt-1">
                    <Tag className="w-5 h-5 text-blue-600" />
                  </div>
                  <p className="text-gray-600 text-sm sm:text-base">
                    <strong className="text-blue-900 block mb-1">Dybdegående:</strong> God mod specifikke smerter, spændinger og sportsskader.
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="bg-emerald-100 p-2 rounded-lg mr-4 mt-1">
                    <Sparkles className="w-5 h-5 text-emerald-600" />
                  </div>
                  <p className="text-gray-600 text-sm sm:text-base">
                    <strong className="text-blue-900 block mb-1">Velvære:</strong> Ideel til afstresning, velvære og mentalt overskud.
                  </p>
                </div>
              </div>
            </div>
            <div className="lg:w-1/2 w-full">
              <div className="bg-blue-900 p-10 rounded-3xl text-white">
                <h3 className="text-2xl font-bold mb-6 text-center">Få gratis rådgivning</h3>
                <p className="text-blue-100 text-sm sm:text-base text-center mb-10">
                  Ring til Carsten for en uforpligtende snak om, hvad der vil gavne din krop bedst.
                </p>
                <div className="space-y-4">
                  <a href={`tel:${CLINIC_INFO.phone}`} className="block w-full text-center bg-emerald-500 hover:bg-emerald-600 text-white py-3.5 md:py-4 rounded-xl font-bold transition-all">
                    Ring nu: {CLINIC_INFO.phone}
                  </a>
                  <Link to="/kontakt" className="block w-full text-center border-2 border-white/20 hover:bg-white/10 text-white py-3.5 md:py-4 rounded-xl font-bold transition-all">
                    Kontakt via e-mail
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
